import { CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import SectionSurface from '../shared/SectionSurface';
import {
  PRICING_HERO,
  PRICING_ENGAGEMENTS,
  PRICING_INCLUDED,
} from '../../../content/enterpriseMessaging';

const ACCENT_RING = {
  migration: 'ring-accent-100 border-accent-100',
  brand: 'ring-brand-100 border-brand-200',
  enterprise: 'ring-brand-100 border-brand-200 bg-gradient-to-br from-brand-50/30 to-accent-50/30',
  semantic: 'ring-[rgba(0,18,63,0.16)] border-[rgba(0,18,63,0.16)]',
};

const ACCENT_BADGE = {
  migration: 'badge-migration',
  brand: 'badge-brand',
  enterprise: 'badge-enterprise',
  semantic: 'badge-semantic',
};

export default function PricingDetails() {
  return (
    <>
      <SectionSurface
        variant="wash"
        as="section"
        decor="none"
        underNav
        bleed="bottom"
        flowBottom
        overlap
      >
        <div className="container-x section">
          <div className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">{PRICING_HERO.eyebrow}</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-ink-900">
              {PRICING_HERO.title}
            </h1>
            <p className="mt-5 text-lg text-ink-600 leading-relaxed">{PRICING_HERO.subtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={PRICING_HERO.primaryCta.href} className="btn-primary btn-lg">
                {PRICING_HERO.primaryCta.label}
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              <a href={PRICING_HERO.secondaryCta.href} className="btn-secondary btn-lg">
                {PRICING_HERO.secondaryCta.label}
              </a>
            </div>
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="muted" as="section" id="engagements" decor="dots" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="text-center mb-12">
            <p className="eyebrow">How we partner</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              Three enterprise tiers, scoped to your outcomes.
            </h2>
            <p className="mt-4 text-lg text-ink-600 max-w-2xl mx-auto leading-relaxed">
              No fixed prices or monthly SaaS tiers. Each model maps to migrations, interactive analytics,
              semantic intelligence, AI agents, and platform engineering, with deliverables defined upfront.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {PRICING_ENGAGEMENTS.slice(0, 3).map((engagement) => {
              const ring = ACCENT_RING[engagement.accent] || ACCENT_RING.brand;
              const badge = ACCENT_BADGE[engagement.accent] || 'badge-brand';
              const featured = engagement.featured;

              return (
                <div
                  key={engagement.id}
                  className={`relative flex flex-col rounded-2xl border bg-white overflow-hidden shadow-sm ring-1 ${ring} ${featured ? 'xl:scale-[1.01] xl:shadow-md' : ''}`}
                >
                  {featured && (
                    <div className="absolute top-4 right-4 sm:top-5 sm:right-5">
                      <span className="badge-brand">Most requested</span>
                    </div>
                  )}
                  <div className="p-6 pb-0 sm:p-8">
                    <span className={badge}>{engagement.badge}</span>
                    <h3 className="mt-4 text-2xl font-semibold tracking-tight text-ink-900">
                      {engagement.name}
                    </h3>
                    <p className="mt-2 text-sm text-ink-500">{engagement.engagementType}</p>
                    <p className="mt-4 text-base text-ink-700 leading-relaxed">{engagement.bestFit}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {engagement.relatedSolutions.map((s) => (
                        <span key={s} className="badge-neutral text-[10px]">
                          {s}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 h-px bg-ink-100" />
                  </div>

                  <div className="p-6 flex-grow sm:p-8">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-3">
                      What&apos;s included
                    </p>
                    <ul className="space-y-2.5 mb-6">
                      {engagement.included.map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-500" />
                          <span className="text-base leading-snug text-ink-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-3">
                      Example deliverables
                    </p>
                    <ul className="space-y-2">
                      {engagement.deliverables.map((item) => (
                        <li key={item} className="text-base text-ink-600 leading-snug pl-3 border-l-2 border-brand-200">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="px-6 pb-6 sm:px-8 sm:pb-8">
                    <a href={engagement.href} className="btn-primary w-full">
                      {engagement.cta}
                      <ArrowRightIcon className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="grid" as="section" decor="contour" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-10">
              <p className="eyebrow">Always included</p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">
                Every engagement, regardless of model.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {PRICING_INCLUDED.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-xl border border-ink-200 bg-white p-5 shadow-xs">
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-50">
                    <CheckIcon className="h-4 w-4 text-brand-600" />
                  </span>
                  <p className="text-base font-medium text-ink-700 leading-snug">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionSurface>

      <SectionSurface variant="subtle" as="section" decor="flow" bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">
              Not sure which model fits?
            </h2>
            <p className="mt-4 text-base text-ink-600 leading-relaxed">
              Most clients begin with a consultation call. We map your current state, align scope to your
              solution category, and propose the right engagement model with clear deliverables. Want the
              operating model behind those engagements? See our{' '}
              <a href="/delivery" className="font-semibold text-brand-600 hover:underline">
                Agile delivery model
              </a>
              .
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/contact?service=pricing-enterprise-engagement" className="btn-primary btn-lg">
                Request consultation
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              <a href="/platform-engineering" className="btn-secondary btn-lg">
                Explore solutions
              </a>
            </div>
          </div>
        </div>
      </SectionSurface>
    </>
  );
}
